import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { APPROVED, DOWNSALE } from 'Router/routes';
import { useFirstLoanOfferQuery } from '../webapi/application/useFirstLoanOfferQuery';
import { useApplicationQuery, useClientOfferQuery } from '../webapi';
import { useConstraints } from '../context';
import getValuetFromQueryString from './getValueFromQueryString';
import useAffiliate from '../webapi/useAffiliate';

type CalculatorContextType = ReturnType<typeof useCalculatorValue>;

export const CalculatorContext = createContext({} as CalculatorContextType);

function useCalculatorValue() {
  const { constraints } = useConstraints();
  const tokenExists = sessionStorage.getItem('token') !== null;

  // react-router
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const { affiliateForm } = useAffiliate();

  // Local state
  const [amount, setAmount] = useState<number>();
  const beforeOpenedAmount = useRef<number>(null);

  const { data: application } = useApplicationQuery(tokenExists);
  const {
    data: clientOffer,
    isLoading: clientOfferIsLoading,
    isError: isClientError,
  } = useClientOfferQuery(
    {
      amount,
      term: constraints?.termInterval.defaultValue,
    },
    tokenExists &&
      constraints !== undefined &&
      ![APPROVED, DOWNSALE].includes(pathname),
  );
  const { data: firstLoanOffer, isLoading: firstLoanOfferIsLoading } =
    useFirstLoanOfferQuery(
      {
        amount,
        term: constraints?.termInterval.defaultValue,
      },
      !tokenExists &&
        amount !== undefined &&
        constraints !== undefined &&
        application === undefined,
    );

  const loading = clientOfferIsLoading || firstLoanOfferIsLoading;

  // Init logic
  const resolvedAmount = useMemo(() => {
    const applicationAmountPending = tokenExists && !application?.amount;

    if (!constraints || applicationAmountPending) return null;

    if (tokenExists && application?.status === 'OPEN' && application.amount) {
      return application.amount;
    }

    if (affiliateForm?.amount) {
      return affiliateForm.amount;
    }

    if (!tokenExists) {
      const queryAmount = getValuetFromQueryString(
        searchParams.get('amount'),
        constraints.amountInterval,
      );
      if (queryAmount !== null) {
        return queryAmount;
      }
    }

    return constraints.amountInterval.defaultValue;
  }, [tokenExists, application, constraints, searchParams, affiliateForm]);

  const setAmountToSessionStorage = useCallback((value: number) => {
    // SessionStorage accepts only string values
    sessionStorage.setItem('calculatorAmount', String(value));
  }, []);

  useEffect(() => {
    if (resolvedAmount !== null && !amount) {
      setAmount(resolvedAmount as number);
      setAmountToSessionStorage(Number(resolvedAmount));
    }
  }, [resolvedAmount, amount]);

  // update amount according to application if it's need
  useEffect(() => {
    if (application?.amount && application?.amount !== amount)
      handleAmountChange(application?.amount);
  }, [application?.amount]);

  // Handlers
  const handleAmountChange = useCallback((newAmount: number) => {
    setAmount(newAmount);
    setAmountToSessionStorage(newAmount);
  }, []);

  const backupCurrentAmount = () => {
    if (typeof amount === 'number') beforeOpenedAmount.current = amount;
  };

  const recoverPreviousAmount = () => {
    if (amount !== beforeOpenedAmount.current) {
      if (beforeOpenedAmount.current) setAmount(beforeOpenedAmount.current);
    }
  };

  const loadAmountFromSessionStorage = useCallback(() => {
    setAmount(Number(sessionStorage.getItem('calculatorAmount')));
  }, []);

  const offer = clientOffer ?? firstLoanOffer;

  return {
    amount,
    backupCurrentAmount,
    recoverPreviousAmount,
    handleAmountChange,
    loadAmountFromSessionStorage,
    offer,
    loading,
    isClientError,
  };
}

export function CalculatorProvider({ children }: { children: ReactNode }) {
  const value = useCalculatorValue();

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
}
